/* ============================================================
   MARAIS — marais-perf.js
   ────────────────────────────────────────────────────────────
   Three perf features bundled together because they share
   plumbing (section swapping + URL handling):

     1. AJAX filtering on collection pages
     2. AJAX pagination on collection pages
     3. Link prefetch on hover (collection cards, pagination,
        filter chips)

   AJAX strategy:
     Shopify's Section Rendering API returns just one section's
     rendered HTML when called with `?section_id=<key>`. We swap
     the inner DOM of #shopify-section-<key> with the response,
     push the clean URL to history, and re-fire a couple of
     custom events so other scripts (lazy images, wishlist
     hearts, etc.) can rebind to the new DOM nodes.

   Event delegation:
     Filter UI is anchor-based (.fcat-opt with href=...) and
     re-renders entirely on every swap, so we delegate every
     listener to `document` — no rebind needed after a swap.

   SECTION_KEY:
     Read from `<div id="shopify-section-...">`'s id attribute,
     which Shopify generates from the JSON template's section
     key (collection.json → "main" → "shopify-section-main").
     Works for collection.json AND any sub-template that uses
     the same section key.

   Loaded globally via theme.liquid (defer). Self-exits when not
   on a collection page (no #shopify-section-* matching a known
   collection section). Mobile + desktop both use the same
   delegated bindings.
   ============================================================ */

(function () {
  'use strict';

  /* ── DOM helpers ── */
  var doc = document;
  function $$(sel, root) { return Array.prototype.slice.call((root || doc).querySelectorAll(sel)); }
  function closest(el, sel) { return el && el.closest ? el.closest(sel) : null; }

  /* ── Locate the collection section once at boot ── */
  // The wrapping <section id="shopify-section-XXX"> is what we swap on AJAX.
  // We search for the known section types this script is meant to handle.
  // Adding a new section type here is the only thing required to extend
  // AJAX filtering to a new template.
  var COLLECTION_SECTION_TYPES = ['marais-sorted-collection', 'main-collection'];

  function findCollectionSection() {
    for (var i = 0; i < COLLECTION_SECTION_TYPES.length; i++) {
      var type = COLLECTION_SECTION_TYPES[i];
      // Section wrappers carry a data-section-type when Shopify renders them
      // — but in this theme the static <section> wrapper is hand-written, so
      // we fall back to looking up by id. The DOM id pattern for JSON-
      // template sections is shopify-section-<key>. Collection.json uses
      // "main" as the key, so we look for that first; then any wrapper that
      // contains a .collection-layout class is treated as the target.
      var byKey = doc.getElementById('shopify-section-main');
      if (byKey && byKey.querySelector('.collection-layout, .collection-page')) {
        return { wrapper: byKey, type: type };
      }
    }
    // Fallback: find the first section wrapper containing a .collection-page
    var pages = $$('.collection-page, .collection-layout');
    for (var j = 0; j < pages.length; j++) {
      var w = closest(pages[j], '[id^="shopify-section-"]');
      if (w) return { wrapper: w, type: 'unknown' };
    }
    return null;
  }

  var section = findCollectionSection();
  if (!section) return;

  // Derive the section_id Shopify wants from the wrapper's DOM id.
  // "shopify-section-main" → "main".
  var SECTION_KEY = section.wrapper.id.replace(/^shopify-section-/, '');
  var sectionWrapper = section.wrapper;

  /* ── AJAX state ── */
  // Token used to discard out-of-order responses. Every new fetch bumps the
  // token; if a slow earlier fetch resolves after a faster later one, we
  // drop it instead of clobbering the user's current view.
  var requestToken = 0;
  // Already-prefetched URLs go in here so hover-then-hover-again on the
  // same target doesn't refetch.
  var prefetched = Object.create(null);

  /* ── Section swap ──
     1. Bump the token
     2. Fetch the target URL with ?section_id=<KEY>
     3. Parse the response, locate the same #shopify-section-<KEY>
     4. Replace sectionWrapper's innerHTML with the response's innerHTML
     5. Re-cache sectionWrapper (DOM node itself is preserved because
        we only swap children — but be defensive)
     6. pushState to the clean URL (no section_id)
     7. Dispatch `marais:section-swapped` so other scripts can rebind */
  function buildSectionUrl(url) {
    // Append ?section_id=X or &section_id=X depending on existing qs
    var sep = url.indexOf('?') >= 0 ? '&' : '?';
    return url + sep + 'section_id=' + encodeURIComponent(SECTION_KEY);
  }

  function setBusy(busy) {
    sectionWrapper.classList.toggle('is-perf-loading', !!busy);
    sectionWrapper.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  function swapSection(url, opts) {
    opts = opts || {};
    var myToken = ++requestToken;
    setBusy(true);

    fetch(buildSectionUrl(url), { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) {
        if (!r.ok) throw new Error('section_fetch_' + r.status);
        return r.text();
      })
      .then(function (html) {
        // If a newer request has fired since this one started, drop it.
        if (myToken !== requestToken) return;

        // Parse via DOMParser rather than `innerHTML =` on a live node:
        // DOMParser sets script elements' "already-started" flag so any
        // <script> in the response won't execute on insertion. This
        // closes an XSS class even though the source is same-origin
        // Shopify (defence-in-depth — protects against future proxy /
        // app-injection cases). The Section Rendering API returns just
        // the section's inner HTML, so we collect parsed.body's children
        // and swap them in via replaceChildren.
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var newKids = Array.prototype.slice.call(parsed.body.childNodes);
        sectionWrapper.replaceChildren.apply(sectionWrapper, newKids);

        // Update URL — strip the section_id we added.
        try {
          var pretty = url;
          if (opts.scrollTop) window.scrollTo({ top: 0, behavior: 'smooth' });
          history.pushState({ marais: true }, '', pretty);
        } catch (e) { /* pushState can throw in some iframe contexts */ }

        setBusy(false);
        // Let other scripts rebind to the freshly-swapped DOM.
        doc.dispatchEvent(new CustomEvent('marais:section-swapped', { detail: { wrapper: sectionWrapper, key: SECTION_KEY } }));
      })
      .catch(function (err) {
        setBusy(false);
        if (myToken !== requestToken) return;
        // Hard fallback: full-page navigation so the user isn't stuck.
        console.warn('[marais-perf] AJAX swap failed, falling back to full load:', err);
        window.location.href = url;
      });
  }

  /* ── Coexistence guard ──
     marais-collection.js owns its own AJAX filter pipeline
     (fetchFilter + #CollectionSidebar / #CollectionMain swap, cache,
     AbortController). When that's loaded on the page, our click
     interception fights it: both fire on .fcat-opt taps, our swap
     replaces the section wrapper (clobbering the mobile drawer's
     wrapper element), and the user sees the drawer close + reopen.
     Detect its presence via the cache global it sets at boot and
     skip our click + popstate handlers so theirs runs unopposed.
     The hover-prefetch + sort handlers below stay active because
     they don't overlap. */
  var THEME_AJAX_ACTIVE = !!window._mhFilterCache;

  /* ── Click delegation ── */
  // We intercept clicks on elements that would otherwise cause a full
  // navigation but where the destination is the same collection with
  // different query parameters. Each selector matches a filter/sort/page
  // affordance. New click sources can be added without re-binding.
  var CLICK_SELECTORS = [
    '.fcat-opt',              // filter option (leaf)
    '.active-chip',           // active filter chip (remove)
    '.sidebar__clear',        // "Clear all filters"
    '.collection-empty__clear', // empty state "Clear filters"
    '.pagination__page',      // page number
    '.pagination__btn',       // prev/next
  ].join(',');

  if (!THEME_AJAX_ACTIVE) doc.addEventListener('click', function (e) {
    // Allow modifier-click / middle-click / new-tab to behave normally.
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button === 1) return;
    var a = closest(e.target, CLICK_SELECTORS);
    if (!a || !a.href) return;

    // Only intercept same-origin nav.
    var url;
    try { url = new URL(a.href, window.location.origin); }
    catch (err) { return; }
    if (url.origin !== window.location.origin) return;

    // Only intercept when the click would land on the SAME pathname OR a
    // path within /collections/* (e.g. clearing all filters routes back
    // to the bare collection URL). Locale-aware: on /zh/ the pathname is
    // /zh/collections/..., so we test against window._mhLocalePrefix +
    // /collections/ instead of the bare /collections/. Empty prefix on
    // the default English locale keeps the check identical.
    var _localePrefix = window._mhLocalePrefix || '';
    var _collectionPath = _localePrefix + '/collections/';
    var samePath = url.pathname === window.location.pathname;
    var sameCollectionRoot = window.location.pathname.indexOf(_collectionPath) === 0
      && url.pathname.indexOf(_collectionPath) === 0;
    if (!samePath && !sameCollectionRoot) return;

    var isPagination = !!closest(a, '.pagination__page, .pagination__btn');

    e.preventDefault();
    swapSection(url.href, { scrollTop: isPagination });
  }, true);

  /* ── Sort dropdown ── */
  // Sort is a <select> change rather than a link click. We listen for
  // change events and synthesise a URL with ?sort_by=<value>.
  if (!THEME_AJAX_ACTIVE) doc.addEventListener('change', function (e) {
    var sel = e.target;
    if (!sel.classList || !sel.classList.contains('sort-select')) return;
    var url = new URL(window.location.href);
    url.searchParams.set('sort_by', sel.value);
    swapSection(url.toString());
  }, true);

  /* ── Browser back / forward ── */
  // When the user uses the browser nav buttons, popstate fires. Swap the
  // section to match the URL — no full reload needed. Skip when the
  // theme owns the AJAX pipeline; it has its own popstate handler.
  if (!THEME_AJAX_ACTIVE) window.addEventListener('popstate', function (e) {
    // If the state wasn't pushed by us, fall back to a real navigation
    // so we don't double-handle.
    if (!e.state || !e.state.marais) {
      window.location.reload();
      return;
    }
    swapSection(window.location.href);
  });

  /* ──────────────────────────────────────────────
     Prefetch on hover
     ────────────────────────────────────────────── */
  // Selectors covering links that, if hovered, the user might click.
  // Product cards on the grid + pagination + filter chips + active chips
  // collectively cover ~95% of next-click destinations.
  var PREFETCH_SELECTORS = [
    '.product-card a',
    '.collection-card a',
    '.pagination__page',
    '.pagination__btn',
    '.fcat-opt',
    '.active-chip',
  ].join(',');

  var HOVER_DELAY_MS = 65;
  var hoverTimer = null;

  function prefetch(url) {
    if (prefetched[url]) return;
    prefetched[url] = true;
    // <link rel="prefetch"> is the lightest mechanism — browsers may
    // dedupe with their own cache, and they only prefetch on idle.
    var link = doc.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    link.as = 'document';
    doc.head.appendChild(link);
  }

  function scheduleHoverPrefetch(href) {
    clearTimeout(hoverTimer);
    hoverTimer = setTimeout(function () { prefetch(href); }, HOVER_DELAY_MS);
  }

  doc.addEventListener('mouseover', function (e) {
    var a = closest(e.target, PREFETCH_SELECTORS);
    if (!a || !a.href) return;
    var url;
    try { url = new URL(a.href, window.location.origin); }
    catch (err) { return; }
    if (url.origin !== window.location.origin) return;
    scheduleHoverPrefetch(url.href);
  }, true);

  doc.addEventListener('mouseout', function () { clearTimeout(hoverTimer); }, true);

  /* ── touchstart variant ── */
  // On touch devices there's no hover, so we kick off the prefetch as soon
  // as the user begins the tap. Browser tap → click latency is ~300ms on
  // some devices, plenty of time for the prefetch to land.
  doc.addEventListener('touchstart', function (e) {
    var a = closest(e.target, PREFETCH_SELECTORS);
    if (!a || !a.href) return;
    var url;
    try { url = new URL(a.href, window.location.origin); }
    catch (err) { return; }
    if (url.origin !== window.location.origin) return;
    prefetch(url.href);
  }, { capture: true, passive: true });
}());
